import { getReferenceString } from "@medplum/core";
import {
  DATA_ABSENT_REASON_SYSTEM,
  memberKey,
  ORDER_ID_URL,
  ORDER_SET_ENTITY_ID_SYSTEM,
  syncKey,
  TREATMENT_OBSERVATION_COMPONENT_CODE_SYSTEM,
  TREATMENT_OBSERVATION_DEFINITION_CODE_SYSTEM,
  TREATMENT_OBSERVATION_ID_SYSTEM,
  TREATMENT_OBSERVATION_KIND,
  TREATMENT_ORDER_ID_URL,
  TREATMENT_ORDER_SET_ID_URL,
  TREATMENT_PROCEDURE_ENTITY_ID_SYSTEM,
  TREATMENT_PROCEDURE_KIND,
  TREATMENT_PROCEDURE_KIND_SYSTEM,
  TREATMENT_TRIAL_ID_SYSTEM,
  TREATMENT_TRIAL_KIND,
  TREATMENT_TRIAL_SOURCE_URL,
  TREATMENT_UNCAPTURED_ID_SYSTEM,
  TREATMENT_UNCAPTURED_KIND,
} from "@repo/api";
import type {
  AdjustTreatmentProcedureTimesInput,
  ClearTreatmentProcedureObservationUnableToCaptureInput,
  CompleteTreatmentProcedureInput,
  CorrectTreatmentProcedureObservationInput,
  MarkTreatmentProcedureDidNotPerformInput,
  MarkTreatmentProcedureObservationUnableToCaptureInput,
  MarkTreatmentProcedureUnableToCompleteInput,
  RecordTreatmentProcedureObservationInput,
  ReopenTreatmentProcedureInput,
  RetractTreatmentProcedureDidNotPerformInput,
  RetractTreatmentProcedureObservationInput,
  SetTreatmentProcedureNoteInput,
  TreatmentObservationInputFacts,
} from "@repo/api/schemas";
import {
  recordTreatmentProcedureObservationInputSchema,
  setTreatmentProcedureNoteInputSchema,
} from "@repo/api/schemas";
import type { Collection } from "@tanstack/db";
import { defineWrite, type WriteOptions } from "../actions/define-write";
import { orderJoinKeys } from "../fhir/collections";
import { adjustProcedurePeriod } from "../fhir/procedure-period";
import type { ObservationRow, ProcedureRow, RequestGroupRow, ServiceRequestRow } from "../fhir/types";
import {
  beginProcedure,
  type KeyedProcedure,
  procedureFor,
  procedureReference,
  type TrialRowKind,
  updateProcedureOrder,
  valueToFhir,
} from "./_procedure-optimistic";
import type { ProcedureTrialKind } from "./_procedure-trial-actions";

type OptimisticCollection<T extends ProcedureRow | ServiceRequestRow | RequestGroupRow | ObservationRow> = Pick<
  Collection<T, string>,
  "state" | "insert" | "update"
>;

interface TreatmentProcedureActionDependencies {
  procedures: () => OptimisticCollection<ProcedureRow>;
  observations: () => OptimisticCollection<ObservationRow>;
  serviceRequests: () => OptimisticCollection<ServiceRequestRow>;
  requestGroups: () => OptimisticCollection<RequestGroupRow>;
  adjustTimes: (input: AdjustTreatmentProcedureTimesInput, options: WriteOptions) => Promise<unknown>;
  complete: (input: CompleteTreatmentProcedureInput, options: WriteOptions) => Promise<unknown>;
  markUnableToComplete: (input: MarkTreatmentProcedureUnableToCompleteInput, options: WriteOptions) => Promise<unknown>;
  markDidNotPerform: (input: MarkTreatmentProcedureDidNotPerformInput, options: WriteOptions) => Promise<unknown>;
  reopen: (input: ReopenTreatmentProcedureInput, options: WriteOptions) => Promise<unknown>;
  retractDidNotPerform: (input: RetractTreatmentProcedureDidNotPerformInput, options: WriteOptions) => Promise<unknown>;
  recordObservation: (input: RecordTreatmentProcedureObservationInput, options: WriteOptions) => Promise<unknown>;
  correctObservation: (input: CorrectTreatmentProcedureObservationInput, options: WriteOptions) => Promise<unknown>;
  retractObservation: (input: RetractTreatmentProcedureObservationInput, options: WriteOptions) => Promise<unknown>;
  markObservationUnableToCapture: (
    input: MarkTreatmentProcedureObservationUnableToCaptureInput,
    options: WriteOptions,
  ) => Promise<unknown>;
  clearObservationUnableToCapture: (
    input: ClearTreatmentProcedureObservationUnableToCaptureInput,
    options: WriteOptions,
  ) => Promise<unknown>;
  setNote: (input: SetTreatmentProcedureNoteInput, options: WriteOptions) => Promise<unknown>;
}

type ProcedureIdentityInput = { orderSetId: string; orderId: string };
/** `procedureId` is the caller-named identity the server begins the procedure under, when it begins one. */
type ProcedureBeginInput = ProcedureIdentityInput & { procedureId?: string };

function orderFor(deps: TreatmentProcedureActionDependencies, input: ProcedureIdentityInput): ServiceRequestRow {
  const order = deps.serviceRequests().state.get(memberKey(input.orderSetId, input.orderId));
  if (!order?.orderId) {
    throw new Error(`TreatmentProcedure '${input.orderId}' has no loaded order in OrderSet '${input.orderSetId}'`);
  }
  return order;
}

function newProcedure(
  deps: TreatmentProcedureActionDependencies,
  input: ProcedureBeginInput,
  status: ProcedureRow["status"],
  fields: Partial<Pick<ProcedureRow, "statusReason" | "note">> = {},
): ProcedureRow {
  const order = orderFor(deps, input);
  const domainId = input.procedureId;
  if (!domainId) throw new Error("A new procedure requires its caller-named identity");
  return {
    resourceType: "Procedure",
    identifier: [{ system: TREATMENT_PROCEDURE_ENTITY_ID_SYSTEM, value: domainId }],
    domainId,
    meta: { tag: [{ system: TREATMENT_PROCEDURE_KIND_SYSTEM, code: TREATMENT_PROCEDURE_KIND }] },
    status,
    ...(fields.statusReason ? { statusReason: fields.statusReason } : {}),
    ...(order.definitionCanonical ? { instantiatesCanonical: [order.definitionCanonical] } : {}),
    code: order.code ?? {},
    subject: order.subject as ProcedureRow["subject"],
    ...(order.encounter ? { encounter: order.encounter } : {}),
    basedOn: [
      {
        ...(order.id ? { reference: getReferenceString(order) } : {}),
        identifier: { system: ORDER_ID_URL, value: order.orderId },
        extension: [{ url: ORDER_SET_ENTITY_ID_SYSTEM, valueString: input.orderSetId }],
      },
    ],
    extension: [
      { url: TREATMENT_ORDER_SET_ID_URL, valueString: input.orderSetId },
      { url: TREATMENT_ORDER_ID_URL, valueString: order.orderId },
    ],
    orderId: order.orderId,
    ...orderJoinKeys(domainId, input.orderSetId, order.orderId),
    ...(fields.note ? { note: fields.note } : {}),
  };
}

function recordFor(
  deps: TreatmentProcedureActionDependencies,
  input: ProcedureIdentityInput,
): ProcedureRow | undefined {
  return procedureFor(deps.procedures(), input);
}

/** The first charting act begins the procedure, matching the handler. */
function procedureRowFor(deps: TreatmentProcedureActionDependencies, input: ProcedureBeginInput): KeyedProcedure {
  return beginProcedure(deps.procedures(), input, () => newProcedure(deps, input, "in-progress"));
}

function childFields(procedure: KeyedProcedure, memberId: string) {
  return {
    subject: procedure.subject as ObservationRow["subject"],
    ...(procedure.encounter ? { encounter: procedure.encounter } : {}),
    partOf: [procedureReference(procedure, TREATMENT_PROCEDURE_ENTITY_ID_SYSTEM)],
    procedureId: procedure.domainId,
    procedureJoinKey: procedure.domainId,
    // Charted against the Procedure, not an order: sentinels keep the row out
    // of every order's observation join rather than in all of them.
    ...orderJoinKeys(memberKey(procedure.domainId, memberId), undefined, undefined),
  };
}

/**
 * An observation is a child ROW keyed `procedureId/memberId`, not a field on the
 * procedure — `buildOrderSetsQuery` joins `observation.procedureJoinKey` to
 * `procedure.domainId`, so this is where the optimistic value has to land for
 * the card to show it. Same shape the diagnostic sibling writes.
 */
function childObservation(
  deps: TreatmentProcedureActionDependencies,
  procedure: KeyedProcedure,
  identity: { system: string; value: string },
): ObservationRow | undefined {
  const row = deps.observations().state.get(memberKey(procedure.domainId, identity.value));
  return row?.identifier?.some((identifier) => identifier.system === identity.system) ? row : undefined;
}

/** ALWAYS `component[]`, never top-level `value[x]` — see the mapper's note. */
function observationFields(input: RecordTreatmentProcedureObservationInput, corrected: boolean) {
  return {
    status: (corrected ? "corrected" : "final") as ObservationRow["status"],
    code: {
      coding: [{ system: TREATMENT_OBSERVATION_DEFINITION_CODE_SYSTEM, code: String(input.definitionId) }],
    },
    effectiveDateTime: input.observedAt,
    component: input.components.map((component) => ({
      code: {
        coding: [{ system: TREATMENT_OBSERVATION_COMPONENT_CODE_SYSTEM, code: String(component.componentId) }],
      },
      ...valueToFhir(component.value),
    })),
    note: input.note ? [{ text: input.note }] : undefined,
  };
}

function observationRecordAction(
  deps: TreatmentProcedureActionDependencies,
  mutate: (input: RecordTreatmentProcedureObservationInput, options: WriteOptions) => Promise<unknown>,
  corrected: boolean,
) {
  return defineWrite<TreatmentObservationInputFacts & { procedureId: string; orderSetId: string; orderId: string }>({
    optimistic: (params) => {
      const input = recordTreatmentProcedureObservationInputSchema.parse(params);
      const procedure = procedureRowFor(deps, input);
      const fields = observationFields(input, corrected);
      const existing = childObservation(deps, procedure, {
        system: TREATMENT_OBSERVATION_ID_SYSTEM,
        value: input.observationId,
      });
      if (existing) {
        deps.observations().update(syncKey(existing), (draft) => {
          draft.status = fields.status;
          draft.code = fields.code;
          draft.effectiveDateTime = fields.effectiveDateTime;
          draft.component = fields.component;
          draft.note = fields.note;
        });
        return;
      }
      deps.observations().insert({
        resourceType: "Observation",
        meta: { tag: [{ system: TREATMENT_PROCEDURE_KIND_SYSTEM, code: TREATMENT_OBSERVATION_KIND }] },
        identifier: [{ system: TREATMENT_OBSERVATION_ID_SYSTEM, value: input.observationId }],
        ...fields,
        ...(input.trialId
          ? {
              derivedFrom: [
                {
                  type: "Observation" as const,
                  identifier: { system: TREATMENT_TRIAL_ID_SYSTEM, value: input.trialId },
                },
              ],
            }
          : {}),
        ...childFields(procedure, input.observationId),
      });
    },
    persist: (input, options) => mutate(recordTreatmentProcedureObservationInputSchema.parse(input), options),
  });
}

const TREATMENT_TRIAL_ROW: TrialRowKind = {
  idSystem: TREATMENT_TRIAL_ID_SYSTEM,
  procedureSystem: TREATMENT_PROCEDURE_ENTITY_ID_SYSTEM,
  sourceUrl: TREATMENT_TRIAL_SOURCE_URL,
  tag: { system: TREATMENT_PROCEDURE_KIND_SYSTEM, code: TREATMENT_TRIAL_KIND },
};

export function createOrderSetTreatmentProcedureActions(deps: TreatmentProcedureActionDependencies) {
  const complete = defineWrite<CompleteTreatmentProcedureInput>({
    optimistic: (input) => {
      updateProcedureOrder(deps, input, "completed");
      const existing = procedureRowFor(deps, input);
      deps.procedures().update(syncKey(existing), (draft) => {
        draft.status = "completed";
      });
    },
    persist: (input, options) => deps.complete(input, options),
  });
  const markUnableToComplete = defineWrite<MarkTreatmentProcedureUnableToCompleteInput>({
    optimistic: (input) => {
      updateProcedureOrder(deps, input, "completed");
      const existing = procedureRowFor(deps, input);
      deps.procedures().update(syncKey(existing), (draft) => {
        draft.status = "stopped";
      });
    },
    persist: (input, options) => deps.markUnableToComplete(input, options),
  });
  const markDidNotPerform = defineWrite<MarkTreatmentProcedureDidNotPerformInput>({
    optimistic: (input) => {
      updateProcedureOrder(deps, input, "revoked");
      const existing = recordFor(deps, input);
      // The order's own row is reused whether it merely began or was struck —
      // the server decides whether anything was charted on it. The period goes
      // because a record that nothing happened cannot carry the time it
      // happened over.
      if (existing) {
        deps.procedures().update(syncKey(existing), (draft) => {
          draft.status = "not-done";
          draft.performedPeriod = undefined;
          draft.performer = undefined;
        });
        return;
      }
      deps.procedures().insert(newProcedure(deps, input, "not-done"));
    },
    persist: (input, options) => deps.markDidNotPerform(input, options),
  });
  const reopen = defineWrite<ReopenTreatmentProcedureInput>({
    optimistic: (input) => {
      updateProcedureOrder(deps, input, "draft");
      const existing = recordFor(deps, input);
      if (!existing) throw new Error(`TreatmentProcedure for order '${input.orderId}' is not loaded`);
      deps.procedures().update(syncKey(existing), (draft) => {
        // Reopening clears the end time; recorded trials remain intact.
        draft.performedPeriod = draft.performedPeriod?.start ? { start: draft.performedPeriod.start } : undefined;
        draft.status = "in-progress";
      });
    },
    persist: (input, options) => deps.reopen(input, options),
  });
  const retractDidNotPerform = defineWrite<RetractTreatmentProcedureDidNotPerformInput>({
    optimistic: (input) => {
      updateProcedureOrder(deps, input, "draft");
      const existing = recordFor(deps, input);
      if (!existing) throw new Error(`TreatmentProcedure for order '${input.orderId}' is not loaded`);
      deps.procedures().update(syncKey(existing), (draft) => {
        draft.status = "entered-in-error";
        draft.performedPeriod = undefined;
      });
    },
    persist: (input, options) => deps.retractDidNotPerform(input, options),
  });
  const setNote = defineWrite<Omit<SetTreatmentProcedureNoteInput, "orderSetId" | "orderId"> & ProcedureIdentityInput>({
    optimistic: (params) => {
      const input = setTreatmentProcedureNoteInputSchema.parse(params);
      const existing = beginProcedure(deps.procedures(), input, () => newProcedure(deps, input, "in-progress"), false);
      deps.procedures().update(syncKey(existing), (draft) => {
        draft.note = input.note ? [{ text: input.note }] : undefined;
      });
    },
    persist: (input, options) => deps.setNote(setTreatmentProcedureNoteInputSchema.parse(input), options),
  });
  const recordObservation = observationRecordAction(deps, deps.recordObservation, false);
  const correctObservation = observationRecordAction(deps, deps.correctObservation, true);
  const retractObservation = defineWrite<RetractTreatmentProcedureObservationInput>({
    optimistic: (input) => {
      const procedure = procedureRowFor(deps, input);
      const existing = childObservation(deps, procedure, {
        system: TREATMENT_OBSERVATION_ID_SYSTEM,
        value: input.observationId,
      });
      if (!existing) throw new Error(`Treatment observation '${input.observationId}' is not loaded`);
      deps.observations().update(syncKey(existing), (draft) => {
        draft.status = "entered-in-error";
      });
    },
    persist: (input, options) => deps.retractObservation(input, options),
  });
  const clearObservationUnableToCapture = defineWrite<ClearTreatmentProcedureObservationUnableToCaptureInput>({
    optimistic: (input) => {
      const procedure = procedureRowFor(deps, input);
      const existing = childObservation(deps, procedure, {
        system: TREATMENT_UNCAPTURED_ID_SYSTEM,
        value: String(input.definitionId),
      });
      if (!existing) throw new Error(`Uncaptured observation '${input.definitionId}' is not loaded`);
      deps.observations().update(syncKey(existing), (draft) => {
        draft.status = "entered-in-error";
      });
    },
    persist: (input, options) => deps.clearObservationUnableToCapture(input, options),
  });
  const markObservationUnableToCapture = defineWrite<MarkTreatmentProcedureObservationUnableToCaptureInput>({
    optimistic: (input) => {
      const procedure = procedureRowFor(deps, input);
      const definitionId = String(input.definitionId);
      // An uncaptured slot is identified by the DECLARATION id under its own
      // identity system, which is why it can never collide with a measurement's.
      const identity = { system: TREATMENT_UNCAPTURED_ID_SYSTEM, value: definitionId };
      const dataAbsentReason = { coding: [{ system: DATA_ABSENT_REASON_SYSTEM, code: "error" }] };
      const existing = childObservation(deps, procedure, identity);
      if (existing) {
        deps.observations().update(syncKey(existing), (draft) => {
          draft.status = "final";
          draft.dataAbsentReason = dataAbsentReason;
        });
        return;
      }
      deps.observations().insert({
        resourceType: "Observation",
        meta: { tag: [{ system: TREATMENT_PROCEDURE_KIND_SYSTEM, code: TREATMENT_UNCAPTURED_KIND }] },
        identifier: [identity],
        status: "final",
        code: { coding: [{ system: TREATMENT_OBSERVATION_DEFINITION_CODE_SYSTEM, code: definitionId }] },
        dataAbsentReason,
        ...childFields(procedure, definitionId),
      });
    },
    persist: (input, options) => deps.markObservationUnableToCapture(input, options),
  });
  return {
    adjustTimes: defineWrite<AdjustTreatmentProcedureTimesInput>({
      optimistic: (input) => {
        const row = recordFor(deps, input);
        if (!row) throw new Error("Treatment procedure is not loaded");
        deps.procedures().update(syncKey(row), (draft) => {
          draft.performedPeriod = adjustProcedurePeriod(draft.performedPeriod, input);
        });
      },
      persist: (input, options) => deps.adjustTimes(input, options),
    }),
    trialKind: {
      row: TREATMENT_TRIAL_ROW,
      begin: (input) => procedureRowFor(deps, input),
    } satisfies ProcedureTrialKind,
    complete,
    markUnableToComplete,
    markDidNotPerform,
    reopen,
    retractDidNotPerform,
    recordObservation,
    correctObservation,
    retractObservation,
    markObservationUnableToCapture,
    clearObservationUnableToCapture,
    setNote,
  };
}
